import React, { useRef, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Alert,
} from "@mui/material";
import { CloudUpload, PhotoCamera } from "@mui/icons-material";
import { WB_SX, WB_UI } from "../../../ui/ux/uiTokens";

/**
 * Quick update for WhatsApp business profile picture only.
 * POST .../business-profile/picture (multipart profile_picture)
 */
const ProfilePictureCard = ({
    currentPictureUrl,
    onSave,
    loading = false,
}) => {
    const inputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [localError, setLocalError] = useState("");

    const handleFileChange = (event) => {
        const next = event.target.files?.[0];
        setLocalError("");
        if (!next) {
            setFile(null);
            setPreview(null);
            return;
        }

        const okType = ["image/jpeg", "image/png", "image/jpg"].includes(next.type);
        if (!okType) {
            setLocalError("Only JPEG or PNG images are allowed.");
            setFile(null);
            setPreview(null);
            return;
        }
        if (next.size > 5 * 1024 * 1024) {
            setLocalError("Image must be 5 MB or smaller.");
            setFile(null);
            setPreview(null);
            return;
        }

        setFile(next);
        setPreview(URL.createObjectURL(next));
    };

    const handleUpload = () => {
        if (!file) return;
        onSave?.(file);
    };

    const displayUrl = preview || currentPictureUrl;

    return (
        <Card elevation={0} sx={{ ...WB_SX.card, height: "100%" }}>
            <CardContent sx={WB_SX.cardContent}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            display: "grid",
                            placeItems: "center",
                            backgroundColor: WB_UI.color.softSuccessBg,
                            color: WB_UI.color.accentGreen,
                            border: `1px solid ${WB_UI.color.border}`,
                        }}
                    >
                        <PhotoCamera fontSize="small" />
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" sx={WB_SX.sectionTitle}>
                            Update Profile Picture
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Convenience API — JPEG/PNG, max 5 MB
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
                    {displayUrl ? (
                        <Box
                            component="img"
                            src={displayUrl}
                            alt="Profile preview"
                            sx={{
                                width: 88,
                                height: 88,
                                borderRadius: 2,
                                objectFit: "cover",
                                border: `1px solid ${WB_UI.color.border}`,
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: 88,
                                height: 88,
                                borderRadius: 2,
                                border: `1px dashed ${WB_UI.color.border}`,
                                display: "grid",
                                placeItems: "center",
                                color: "text.secondary",
                                bgcolor: WB_UI.color.softInfoBg,
                            }}
                        >
                            <PhotoCamera />
                        </Box>
                    )}
                    <Box>
                        <input
                            ref={inputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/jpg"
                            hidden
                            onChange={handleFileChange}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<CloudUpload />}
                            onClick={() => inputRef.current?.click()}
                            sx={WB_SX.secondaryButton}
                            disabled={loading}
                        >
                            Choose Image
                        </Button>
                        {file && (
                            <Typography variant="caption" display="block" sx={{ mt: 0.75 }} color="text.secondary">
                                {file.name} ({Math.round(file.size / 1024)} KB)
                            </Typography>
                        )}
                    </Box>
                </Box>

                {localError && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                        {localError}
                    </Alert>
                )}

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                        variant="contained"
                        startIcon={<CloudUpload />}
                        onClick={handleUpload}
                        disabled={loading || !file}
                        sx={WB_SX.primaryButton}
                    >
                        Upload Picture
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

export default ProfilePictureCard;
